import React, { useCallback, useEffect, useRef, useState } from "react";
import { Box, SxProps, Theme } from "@mui/material";

const STORAGE_PREFIX = "modern-resize:";

export interface ResizablePanelProps {
  /** Unique key per block (page-scoped recommended). */
  storageKey: string;
  children: React.ReactNode;
  /** Used when nothing is stored yet; panel grows with content if height omitted. */
  defaultHeight?: number;
  defaultWidth?: number;
  minWidth?: number;
  minHeight?: number;
  maxWidth?: number | string;
  sx?: SxProps<Theme>;
}

/**
 * Drag the bottom-right grip to resize a content block; size persists in sessionStorage.
 */
const ResizablePanel: React.FC<ResizablePanelProps> = ({
  storageKey,
  children,
  defaultHeight,
  defaultWidth,
  minWidth = 260,
  minHeight = 200,
  maxWidth = "100%",
  sx,
}) => {
  const fullKey = `${STORAGE_PREFIX}${storageKey}`;
  const [size, setSize] = useState<{ w?: number; h?: number }>(() => {
    try {
      const raw = sessionStorage.getItem(fullKey);
      if (!raw) return {};
      const parsed = JSON.parse(raw) as { w?: number; h?: number };
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  });

  const dragging = useRef(false);
  const start = useRef({ x: 0, y: 0, w: 0, h: 0 });

  const persist = useCallback(
    (next: { w?: number; h?: number }) => {
      setSize(next);
      try {
        sessionStorage.setItem(fullKey, JSON.stringify(next));
      } catch {
        /* ignore quota / private mode */
      }
    },
    [fullKey]
  );

  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current) return;
      const dx = e.clientX - start.current.x;
      const dy = e.clientY - start.current.y;
      const nw = Math.max(minWidth, Math.round(start.current.w + dx));
      const nh = Math.max(minHeight, Math.round(start.current.h + dy));
      persist({ w: nw, h: nh });
    };
    const onUp = () => {
      dragging.current = false;
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
  }, [minHeight, minWidth, persist]);

  const onHandleDown = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const host = (e.currentTarget as HTMLElement).parentElement;
    if (!host) return;
    const rect = host.getBoundingClientRect();
    dragging.current = true;
    start.current = {
      x: e.clientX,
      y: e.clientY,
      w: size.w ?? rect.width,
      h: size.h ?? rect.height,
    };
  };

  return (
    <Box
      sx={{
        position: "relative",
        boxSizing: "border-box",
        width: size.w !== undefined ? `${size.w}px` : defaultWidth ? `${defaultWidth}px` : "100%",
        maxWidth,
        height:
          size.h !== undefined
            ? `${size.h}px`
            : defaultHeight !== undefined
              ? `${defaultHeight}px`
              : undefined,
        minHeight:
          size.h !== undefined ? minHeight : defaultHeight !== undefined ? minHeight : 0,
        minWidth,
        alignSelf: "stretch",
        ...sx,
      }}
    >
      <Box
        sx={{
          height: "100%",
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {children}
      </Box>
      <Box
        role="presentation"
        aria-label="Resize panel"
        onMouseDown={onHandleDown}
        sx={{
          position: "absolute",
          right: 0,
          bottom: 0,
          width: 18,
          height: 18,
          cursor: "nwse-resize",
          zIndex: 2,
          background: "linear-gradient(135deg, transparent 50%, rgba(0,0,0,0.18) 50%)",
          borderBottomRightRadius: 8,
          "&:hover": {
            background:
              "linear-gradient(135deg, transparent 45%, rgba(25,118,210,0.35) 45%)",
          },
        }}
      />
    </Box>
  );
};

export default ResizablePanel;
